import { Box, Button, Flex, Text, Loader, Stack, Container } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepProps } from './DrawerFlow';

type DrawerFlowScreenLayoutProps<T> = StepProps<T> & {
  title: string;
  description?: string;
  children: React.ReactNode;
  continueText?: string;
  isLoading?: boolean;
  isSubmitting?: boolean;
  clientNameSuffix?: string;
  footer?: React.ReactNode;
  hideProgress?: boolean;
  allowVerticalOverflow?: boolean;
};

export default function DrawerFlowScreenLayout<T>({
  title,
  description,
  children,
  canGoBack,
  canGoForward,
  previousStep,
  nextStep,
  renderProgress,
  data,
  continueText = 'Continue',
  showClientName = true,
  clientNamePrefix,
  isLoading = false,
  isSubmitting = false,
  clientNameSuffix = 'report',
  footer,
  hideProgress = false,
  allowVerticalOverflow = false,
  maxContainerWidth,
}: DrawerFlowScreenLayoutProps<T>) {
  const client = (data as { client?: { firstName?: string } })?.client;
  const clientName = [clientNamePrefix, `${client?.firstName}'s ${clientNameSuffix}`]
    .filter(Boolean)
    .join(' ');

  const contentArea = (
    <Flex direction="column" flex={1} h="100%" gap="md" mih={0}>
      <Flex direction="column" gap="xxxs">
        <Box>
          <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10" display="inline">
            {title}
          </Text>
          {showClientName && client != null && (
            <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.6" display="inline">
              &nbsp;{clientName}
            </Text>
          )}
        </Box>
        {description && (
          <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.8">
            {description}
          </Text>
        )}
      </Flex>

      <Flex
        direction="column"
        flex={1}
        mih={0}
        h="100%"
        style={{
          overflowY: allowVerticalOverflow ? 'auto' : 'hidden',
          maxHeight: allowVerticalOverflow ? 'none' : '100%',
        }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" h="100%">
            <Loader data-testid={'loader'} />
          </Flex>
        ) : (
          children
        )}
      </Flex>

      <Stack gap="sm">
        {footer != null && footer}
        <Flex justify="space-between" align="center" gap="md">
          {canGoBack && (
            <Button variant="outline-black" size="xl" onClick={() => previousStep()} p="sm">
              <IconArrowLeft size={24} />
            </Button>
          )}

          <Button
            fullWidth
            onClick={() => nextStep()}
            disabled={!canGoForward || isSubmitting}
            size="xl"
            leftSection={isSubmitting ? <Loader size="sm" color="gray.4" /> : undefined}
          >
            {continueText}
          </Button>
        </Flex>
      </Stack>
    </Flex>
  );

  return (
    <Flex flex={1} gap="xxxl" style={{ opacity: isSubmitting ? 0.5 : 1 }} mih={0}>
      {maxContainerWidth ? (
        <Container
          size={maxContainerWidth}
          px={0}
          flex={1}
          style={{
            marginLeft: 0,
            marginRight: 'auto',
          }}
        >
          {contentArea}
        </Container>
      ) : (
        contentArea
      )}

      {!hideProgress && (
        <Flex align="center" justify="center" flex={1} bg="gray.0" style={{ borderRadius: 16 }}>
          {renderProgress?.({ isSubmitting })}
        </Flex>
      )}
    </Flex>
  );
}
